// FAQCategoryView.jsx — страница раздела F.A.Q. со списком статей.
// backLink + шапка категории (с кнопкой «Написать» для владельца) +
// поиск по разделу с подсказками + сетка карточек статей.

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AdminService from '../../../API/AdminService';
import UserService from '../../../API/UserService';
import cl from './FAQCategoryView.module.css';
import logger from '../../../utils/logger';
import {
    IconArrowLeft, IconHelp, IconSearch, IconClose, IconRefresh,
    IconEdit, IconAlert, IconBook, IconClock, IconMessage,
    IconGamepad, IconChart, IconShieldCheck, IconArrowRight,
} from '../../../components/Icons';

// Иконка и тон rail-бара по имени категории (как на главной FAQ)
const CATEGORY_VIEW = {
    integration:  { icon: <IconArrowRight width={22} height={22}/>, tone: 'blue' },
    programming:  { icon: <IconChart width={22} height={22}/>,      tone: 'green' },
    robotics:     { icon: <IconShieldCheck width={22} height={22}/>,tone: 'red' },
    admin_panel:  { icon: <IconBook width={22} height={22}/>,        tone: 'gold' },
    minecraftia:  { icon: <IconGamepad width={22} height={22}/>,    tone: 'violet' },
};
function getCategoryView(name) {
    return CATEGORY_VIEW[name] || { icon: <IconBook width={22} height={22}/>, tone: 'gray' };
}

const FAQCategoryView = () => {
    const navigate = useNavigate();
    const { categoryName } = useParams();

    const [category, setCategory] = useState(null);
    const [articles, setArticles] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchSuggestions, setSearchSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [currentUser, setCurrentUser] = useState(null);

    useEffect(() => {
        loadData();
        loadCurrentUser();
    }, [categoryName]);

    const loadCurrentUser = async () => {
        try {
            const userInfo = await UserService.getMyProfile();
            setCurrentUser(userInfo);
        } catch (err) {
            logger.error('Ошибка получения профиля пользователя:', err);
        }
    };

    const loadData = async () => {
        try {
            setLoading(true);
            const [categoriesResponse, articlesResponse] = await Promise.all([
                AdminService.getFAQCategories(),
                AdminService.getFAQArticles(categoryName),
            ]);

            const foundCategory = categoriesResponse.data.find(cat => cat.name === categoryName);
            if (!foundCategory) {
                setError('Категория не найдена');
                return;
            }

            setCategory(foundCategory);
            setArticles(articlesResponse.data || []);
            setError('');
        } catch (err) {
            setError('Ошибка загрузки данных');
            logger.error('Ошибка загрузки данных FAQ:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleSearchChange = async (e) => {
        const query = e.target.value;
        setSearchQuery(query);

        if (query.length >= 2) {
            try {
                const response = await AdminService.getFAQSearchSuggestions(query, categoryName);
                setSearchSuggestions(response.data || []);
                setShowSuggestions(true);
            } catch (err) {
                logger.error('Ошибка получения подсказок:', err);
                setSearchSuggestions([]);
            }
        } else {
            setSearchSuggestions([]);
            setShowSuggestions(false);
        }
    };

    const handleSearchSubmit = async (e) => {
        e.preventDefault();
        if (searchQuery.trim()) {
            try {
                const response = await AdminService.getFAQArticles(categoryName, searchQuery.trim());
                setArticles(response.data || []);
            } catch (err) {
                logger.error('Ошибка поиска:', err);
            }
        } else {
            loadData();
        }
        setShowSuggestions(false);
    };

    const handleSuggestionClick = (suggestion) => {
        navigate(`/main/faq/articles/${suggestion.id}`);
        setShowSuggestions(false);
        setSearchQuery('');
    };

    const clearSearch = () => {
        setSearchQuery('');
        setSearchSuggestions([]);
        setShowSuggestions(false);
        loadData();
    };

    const handleArticleClick = (articleId) => {
        navigate(`/main/faq/articles/${articleId}`);
    };

    const handleCreateArticle = () => {
        navigate(`/main/faq/create?category=${categoryName}`);
    };

    const handleEditArticle = (articleId) => {
        navigate(`/main/faq/edit/${articleId}`);
    };

    const isOwner = currentUser && currentUser.email === 'min@ya.ru';

    if (loading) {
        return (
            <div className={cl.page_wrapper}>
                <div className={cl.wrap_content_1}>
                    <div className={cl.loadingContainer}>
                        <div className={cl.spinner}/>
                        <p>Загрузка статей…</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={cl.page_wrapper}>
                <div className={cl.wrap_content_1}>
                    <div className={cl.errorState}>
                        <span className={cl.errorIcon}>
                            <IconAlert width={20} height={20}/>
                        </span>
                        <h3>Не удалось загрузить раздел</h3>
                        <p>{error}</p>
                        <button onClick={() => navigate('/main/faq')} className={cl.retryButton}>
                            <IconArrowLeft width={14} height={14}/>
                            <span>Вернуться к F.A.Q.</span>
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const view = getCategoryView(category?.name);
    const articlesCount = articles.length;

    return (
        <div className={cl.page_wrapper}>
            <div className={cl.wrap_content_1}>
                <button
                    onClick={() => navigate('/main/faq')}
                    className={cl.backLink}
                >
                    <IconArrowLeft width={14} height={14}/>
                    <span>К F.A.Q.</span>
                </button>

                {/* Шапка категории */}
                <div className={`${cl.categoryHeader} ${cl[`categoryHeader_${view.tone}`]}`}>
                    <div className={cl.categoryHeaderIcon}>{view.icon}</div>
                    <div className={cl.categoryHeaderText}>
                        <div className={cl.categoryHeaderTopRow}>
                            <h1 className={cl.categoryTitle}>
                                {category?.title || 'Раздел F.A.Q.'}
                            </h1>
                            <span className={cl.pageHeaderPill}>
                                <span className={cl.pageHeaderPillStrong}>{articlesCount}</span>
                                <span className={cl.pageHeaderPillMuted}>
                                    {pluralRu(articlesCount, 'статья', 'статьи', 'статей')}
                                </span>
                            </span>
                        </div>
                        {category?.description && (
                            <p className={cl.categoryDescription}>{category.description}</p>
                        )}
                    </div>
                    {isOwner && (
                        <div className={cl.categoryHeaderActions}>
                            <button onClick={handleCreateArticle} className={cl.btnPrimary}>
                                <IconEdit width={14} height={14}/>
                                <span>Написать статью</span>
                            </button>
                        </div>
                    )}
                </div>

                {/* Поиск с подсказками */}
                <div className={cl.searchCard}>
                    <form onSubmit={handleSearchSubmit} className={cl.searchForm}>
                        <span className={cl.searchInputIcon}>
                            <IconSearch width={18} height={18}/>
                        </span>
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={handleSearchChange}
                            placeholder={category?.title
                                ? `Поиск по разделу «${category.title}»…`
                                : 'Поиск по статьям…'}
                            className={cl.searchInput}
                            onFocus={() => searchSuggestions.length > 0 && setShowSuggestions(true)}
                            onBlur={() => setTimeout(() => setShowSuggestions(false), 180)}
                        />
                        {searchQuery && (
                            <button
                                type="button"
                                onClick={clearSearch}
                                className={cl.searchClearButton}
                                aria-label="Очистить"
                            >
                                <IconClose width={13} height={13}/>
                            </button>
                        )}
                        <button
                            type="submit"
                            className={cl.searchSubmitButton}
                            disabled={!searchQuery.trim()}
                        >
                            <IconSearch width={14} height={14}/>
                            <span className={cl.searchSubmitText}>Найти</span>
                        </button>

                        {showSuggestions && searchSuggestions.length > 0 && (
                            <div className={cl.suggestions}>
                                {searchSuggestions.map(suggestion => (
                                    <button
                                        key={suggestion.id}
                                        type="button"
                                        className={cl.suggestionItem}
                                        onMouseDown={() => handleSuggestionClick(suggestion)}
                                    >
                                        <IconHelp width={14} height={14} className={cl.suggestionIcon}/>
                                        <span className={cl.suggestionTitle}>{suggestion.title}</span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </form>
                </div>

                {/* Список статей */}
                {articles.length === 0 ? (
                    <div className={cl.emptyState}>
                        <div className={cl.emptyIconWrap}>
                            <IconBook width={28} height={28}/>
                        </div>
                        <h2 className={cl.emptyTitle}>Статей пока нет</h2>
                        <p className={cl.emptyDescription}>
                            В этом разделе пока нет опубликованных статей.
                        </p>
                        {isOwner && (
                            <button
                                onClick={handleCreateArticle}
                                className={cl.createFirstArticle}
                            >
                                <IconEdit width={14} height={14}/>
                                <span>Написать первую статью</span>
                            </button>
                        )}
                    </div>
                ) : (
                    <div className={cl.articlesGrid}>
                        {articles.map(article => (
                            <FAQArticleCard
                                key={article.id}
                                article={article}
                                isOwner={isOwner}
                                onView={() => handleArticleClick(article.id)}
                                onEdit={() => handleEditArticle(article.id)}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};


// ----- Карточка статьи -----
const FAQArticleCard = ({ article, isOwner, onView, onEdit }) => {
    return (
        <article className={cl.articleCard}>
            <button
                type="button"
                className={cl.articleClickArea}
                onClick={onView}
            >
                <div className={cl.articleBody}>
                    <div className={cl.articleHead}>
                        <span className={cl.articleIcon}>
                            <IconBook width={16} height={16}/>
                        </span>
                        <h3 className={cl.articleTitle}>{article.title}</h3>
                    </div>
                    {article.summary && (
                        <p className={cl.articleSummary}>{article.summary}</p>
                    )}
                    <div className={cl.articleFooter}>
                        <span className={cl.footerItem}>
                            <IconClock width={11} height={11}/>
                            {AdminService.formatReadTime(article.estimated_read_time)}
                        </span>
                        <span className={cl.footerItem}>
                            <IconMessage width={11} height={11}/>
                            {article.comments_count} {pluralRu(article.comments_count, 'комментарий', 'комментария', 'комментариев')}
                        </span>
                    </div>
                </div>
            </button>

            {isOwner && (
                <button
                    onClick={onEdit}
                    className={cl.articleEditButton}
                    title="Редактировать статью"
                    aria-label="Редактировать"
                >
                    <IconEdit width={14} height={14}/>
                </button>
            )}
        </article>
    );
};

function pluralRu(n, one, few, many) {
    const mod10 = n % 10;
    const mod100 = n % 100;
    if (mod10 === 1 && mod100 !== 11) return one;
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;
    return many;
}

export default FAQCategoryView;
